import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Award, FileText, Download, Beaker, ArrowRight } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
export function LayerXRay() {
  return <section className="py-24 bg-solventum-dark" data-id="element-234">
      <div className="max-w-7xl mx-auto px-6" data-id="element-235">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-236">
          <div className="flex items-center gap-3 mb-4" data-id="element-237">
            <div className="bg-solventum-green text-solventum-dark px-4 py-1 rounded-full text-sm font-bold" data-id="element-238">
              LAYER 3
            </div>
            <Eye className="w-5 h-5 text-solventum-green" data-id="element-239" />
            <span className="text-solventum-lightTeal/60 text-xs tracking-[0.2em] uppercase" data-id="element-240">
              Mobile · X-Ray Toggle
            </span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-241">
            The X-Ray Overlay
          </h2>
          <p className="text-xl text-solventum-lightTeal/80 max-w-3xl" data-id="element-242">
            A swipe-up sheet borrowed from premium streaming. Toggle X-Ray and
            the case stays untouched while a layer of context, credentials and
            downloads rises into view.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 bg-gradient-to-br from-[#01403A] via-solventum-dark to-[#011A16] py-16 px-6" data-id="element-243">
          <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: 'radial-gradient(circle at 70% 50%, rgba(5,221,77,0.18) 0%, transparent 55%)'
        }} data-id="element-244" />

          <div className="relative flex flex-col lg:flex-row items-center justify-center gap-10" data-id="element-245">
            <div className="relative" data-id="element-246">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-white/10 backdrop-blur text-white text-[10px] font-semibold tracking-[0.2em] uppercase rounded-full border border-white/10 z-10" data-id="element-247">
                X-Ray off
              </div>
              <PhoneFrame data-id="element-248">
                <PlayingState data-id="element-249" />
              </PhoneFrame>
            </div>

            <div className="flex flex-col items-center gap-2" data-id="element-250">
              <div className="text-solventum-green text-[10px] font-bold tracking-[0.25em] uppercase" data-id="element-251">
                Tap X-Ray
              </div>
              <div className="w-12 h-12 rounded-full bg-solventum-green/15 ring-1 ring-solventum-green/40 flex items-center justify-center" data-id="element-252">
                <ArrowRight className="w-5 h-5 text-solventum-green hidden lg:block" data-id="element-253" />
                <Eye className="w-5 h-5 text-solventum-green lg:hidden" data-id="element-254" />
              </div>
            </div>

            <div className="relative" data-id="element-255">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-solventum-green text-solventum-dark text-[10px] font-bold tracking-[0.2em] uppercase rounded-full z-10" data-id="element-256">
                X-Ray on
              </div>
              <PhoneFrame data-id="element-257">
                <XRayState data-id="element-258" />
              </PhoneFrame>
            </div>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-solventum-lightTeal/60 mt-12 italic" data-id="element-259">
          The differentiator — rich context, downloadable assets, sales
          enablement signal — without ever disrupting the clinical core.
        </motion.p>
      </div>
    </section>;
}
function PlayingState() {
  return <div className="relative w-full h-full bg-solventum-dark overflow-hidden" data-id="element-260">
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/gFKmuReCYVi2tZ526ffVui/Sk%C3%A6rmbillede_2026-05-11_kl._12.48.57.png')"
    }} data-id="element-261" />
      <div className="absolute inset-0 bg-gradient-to-t from-solventum-dark/70 via-transparent to-solventum-dark/30" data-id="element-262" />

      {/* Top label */}
      <div className="absolute top-3 left-4" data-id="element-263">
        <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="h-3.5 w-auto" data-id="element-264" />
      </div>

      {/* X-Ray button (off state) */}
      <div className="absolute top-3 right-4 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur flex items-center gap-1" data-id="element-265">
        <Eye className="w-2.5 h-2.5 text-white/70" data-id="element-266" />
        <span className="text-white/70 text-[9px] font-semibold tracking-wider uppercase" data-id="element-267">
          X-Ray
        </span>
      </div>

      <div className="absolute bottom-12 left-4 right-4" data-id="element-268">
        <div className="text-solventum-green text-[8px] font-bold tracking-[0.25em] uppercase mb-1" data-id="element-269">
          Chapter 09
        </div>
        <p className="text-white text-[11px] font-medium leading-snug" data-id="element-270">
          Sectional matrix and Incidental Ring to restore distal preparation in
          second premolar. After adhesive procedure using 3M™ Scotchbond™
          Universal Plus Adhesive, Solventum™ Filtek™ Easy Match Flowable
          Restorative and 3M™ Filtek™ Easy Match Universal Restorative, both
          in Bright shade.
        </p>
      </div>

      <div className="absolute bottom-4 left-4 right-4" data-id="element-271">
        <div className="h-[3px] bg-white/20 rounded-full overflow-hidden" data-id="element-272">
          <div className="h-full w-[58%] bg-solventum-green rounded-full" data-id="element-273" />
        </div>
      </div>
    </div>;
}
function XRayState() {
  return <div className="relative w-full h-full bg-solventum-dark overflow-hidden" data-id="element-274">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/gFKmuReCYVi2tZ526ffVui/Sk%C3%A6rmbillede_2026-05-11_kl._12.48.57.png')"
    }} data-id="element-275" />
      <div className="absolute inset-0 bg-solventum-dark/60" data-id="element-276" />

      {/* X-Ray on indicator */}
      <div className="absolute top-3 right-4 px-2.5 py-1 rounded-full bg-solventum-green flex items-center gap-1" data-id="element-277">
        <Eye className="w-2.5 h-2.5 text-solventum-dark" data-id="element-278" />
        <span className="text-solventum-dark text-[9px] font-bold tracking-wider uppercase" data-id="element-279">
          X-Ray
        </span>
      </div>

      {/* Top brand */}
      <div className="absolute top-3 left-4" data-id="element-280">
        <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="h-3.5 w-auto" data-id="element-281" />
      </div>

      {/* Bottom sheet content */}
      <div className="absolute inset-x-0 bottom-0 top-12 px-3 pb-6 flex flex-col gap-2 overflow-hidden" data-id="element-282">
        <div className="w-8 h-1 rounded-full bg-white/30 mx-auto mt-2 mb-1" data-id="element-283" />

        {/* Clinician */}
        <div className="bg-white/[0.06] backdrop-blur rounded-lg p-2.5 border border-white/10" data-id="element-284">
          <div className="flex items-center gap-2 mb-2" data-id="element-285">
            <div className="w-8 h-8 rounded-full bg-cover bg-center ring-1 ring-solventum-green/50" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&auto=format&fit=crop&q=80')"
          }} data-id="element-286" />
            <div className="flex-1 min-w-0" data-id="element-287">
              <div className="text-white text-[10px] font-semibold leading-tight truncate" data-id="element-288">
                Dr Chris O'Connor
              </div>
              <div className="text-white/50 text-[8px]" data-id="element-289">
                Restorative · Newcastle
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-white/70 text-[9px] mb-1" data-id="element-290">
            <Award className="w-2.5 h-2.5 text-solventum-medTeal" data-id="element-291" />
            <span data-id="element-292">DDS · 18 yrs · 27 publications</span>
          </div>
        </div>

        {/* Case details */}
        <div className="bg-white/[0.06] backdrop-blur rounded-lg p-2.5 border border-white/10" data-id="element-293">
          <div className="text-solventum-medTeal text-[8px] font-bold tracking-[0.2em] uppercase mb-1.5" data-id="element-294">
            Case Details
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[9px]" data-id="element-295">
            <div className="text-white/50" data-id="element-296">Tooth</div>
            <div className="text-white text-right font-medium" data-id="element-297">#11</div>
            <div className="text-white/50" data-id="element-298">Indication</div>
            <div className="text-white text-right font-medium" data-id="element-299">Class IV</div>
            <div className="text-white/50" data-id="element-300">Technique</div>
            <div className="text-white text-right font-medium" data-id="element-301">Layered</div>
            <div className="text-white/50" data-id="element-302">Chair time</div>
            <div className="text-white text-right font-medium" data-id="element-303">~75 min</div>
          </div>
        </div>

        {/* Materials */}
        <div className="bg-white/[0.06] backdrop-blur rounded-lg p-2.5 border border-white/10" data-id="element-304">
          <div className="text-solventum-medTeal text-[8px] font-bold tracking-[0.2em] uppercase mb-1.5" data-id="element-305">
            Materials Used
          </div>
          <div className="space-y-1.5" data-id="element-306">
            <div className="flex items-start gap-1.5 text-white/85 text-[9px] leading-snug" data-id="element-307">
              <Beaker className="w-2.5 h-2.5 text-solventum-green flex-shrink-0 mt-0.5" data-id="element-308" />
              <span data-id="element-309">
                Filtek™ Easy Match Universal Restorative Bright Shade
              </span>
            </div>
            <div className="flex items-start gap-1.5 text-white/85 text-[9px] leading-snug" data-id="element-310">
              <Beaker className="w-2.5 h-2.5 text-solventum-green flex-shrink-0 mt-0.5" data-id="element-311" />
              <span data-id="element-312">Solventum™ Filtek™ Easy Match Flowable Restorative</span>
            </div>
          </div>
        </div>

        {/* Downloads */}
        <div className="grid grid-cols-2 gap-1.5" data-id="element-313">
          <div className="bg-solventum-green text-solventum-dark rounded-lg px-2 py-1.5 flex items-center justify-center gap-1" data-id="element-314">
            <Download className="w-2.5 h-2.5" data-id="element-315" />
            <span className="text-[9px] font-bold" data-id="element-316">Step PDF</span>
          </div>
          <div className="bg-white/10 text-white border border-white/15 rounded-lg px-2 py-1.5 flex items-center justify-center gap-1" data-id="element-317">
            <FileText className="w-2.5 h-2.5" data-id="element-318" />
            <span className="text-[9px] font-bold" data-id="element-319">Bio</span>
          </div>
        </div>
      </div>
    </div>;
}